import * as React from "react";

import { convertFromRaw, convertToRaw, EditorState } from "draft-js";
import RichEditor from "../../components/TextEditor/rich_text_editor";
import useBackend from "../../hooks/use_backend";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { fetchSiteData } from "../../scripts/api_v2/site_client";
import { EditableCard } from "../general/editable_card";
import Snackbar from "../../scripts/snackbar";
import { setSiteNotes } from "../../scripts/api_v2/notes_client";
import { useSiteData } from "../../hooks/use_site";

export function NotesCard({ tooltip, permissionMap, required_privilege }) {
  const [editing, setEditing] = React.useState(false);
  const backend = useBackend();
  const params = useParams();
  const queryClient = useQueryClient();

  const { data: siteData, error, isLoading, refetch } = useSiteData();

  const [notes, setNotes] = React.useState(
    siteData?.notes ? EditorState.createWithContent(convertFromRaw(siteData.notes)) : EditorState.createEmpty(),
  );

  const { mutate: notesMutation, isPending: isMutating } = useMutation({
    mutationFn: async ({ rawData }) => {
      return await setSiteNotes(backend.fetch, params.site_id, rawData);
    },
    onMutate: async ({ rawData }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id] });
      // Snapshot the previous value
      const oldSiteData = await queryClient.getQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id]);

      // Optimistically update to the new value
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
        const updated = { ...prev };
        updated.config.notes = rawData;
        return updated;
      });
      // return snapshot for onError
      return { oldSiteData };
    },
    onError: async (err, variables, context) => {
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], context.oldSiteData);
      handleCancelNotes();
      Snackbar.error("Could not update notes: " + err);
    },
    // Always refetch after commit, error or success:
    onSettled: (data, error, variables) => {
      handleCancelNotes();
    },
  });

  async function handleCommitNotes() {
    const mutationData = { rawData: convertToRaw(notes.getCurrentContent()) };
    notesMutation(mutationData);
  }

  function handleCancelNotes() {
    setNotes(
      siteData?.notes ? EditorState.createWithContent(convertFromRaw(siteData.notes)) : EditorState.createEmpty(),
    );
    setEditing(false);
  }

  if (!siteData) return null;

  return (
    <EditableCard
      tooltip={tooltip}
      editable
      title="Installation Notes"
      width={"100%"}
      elevation={0}
      editing={editing}
      handleEdit={() => setEditing(true)}
      handleSave={handleCommitNotes}
      handleCancel={handleCancelNotes}
      permissionMap={permissionMap}
      sx={{ mt: 2 }}
      required_privilege={required_privilege}
      blockDuringSturing={false}
    >
      {editing ? (
        <RichEditor editorState={notes} setEditorState={setNotes} color="black" readOnly={false} />
      ) : (
        <RichEditor editorState={notes} color="black" readOnly={true} />
      )}
    </EditableCard>
  );
}
